/**
 * Canonical TanStack Query keys for the Wiki domain.
 *
 * Centralized so a future refetch / cache-bust touches one file rather
 * than hunting through every hook. Shape mirrors the plan §4.12:
 *
 *   ['wiki', 'tree']
 *   ['wiki', 'doc', docId, 'meta', version]
 *   ['wiki', 'doc', docId, 'body', version]
 *
 * The `version` discriminator on doc-scoped keys ('draft' | 'published')
 * lets the version dropdown switch versions without colliding with the
 * editor's currently-loaded view. Wave 1C ships only the 'draft' default;
 * Wave 2's version viewer will use 'published' (and Phase 3 will extend
 * with `versions/<versionId>` for the diff surface).
 */

export type WikiDocVersionSelector = 'draft' | 'published'

export const WIKI_TREE_QUERY_KEY = ['wiki', 'tree'] as const

export const wikiDocMetaQueryKey = (docId: string, version: WikiDocVersionSelector = 'draft') =>
  ['wiki', 'doc', docId, 'meta', version] as const

export const wikiDocBodyQueryKey = (docId: string, version: WikiDocVersionSelector = 'draft') =>
  ['wiki', 'doc', docId, 'body', version] as const

/**
 * The pretty-URL resolver's cache (`GET /api/wiki/docs/by-tail/:tail`),
 * keyed by the 8-char id tail. Seeded on create so a new page opens on
 * its pretty URL without a resolver round-trip.
 */
export const wikiDocByTailQueryKey = (tail: string | null) => ['wiki', 'doc-by-tail', tail] as const

/**
 * Prefix-shape used by realtime listeners that want to invalidate
 * "everything about this doc" in one call. TanStack Query's
 * `invalidateQueries({ queryKey: wikiDocPrefix(id) })` matches both the
 * meta + body branches above.
 */
export const wikiDocPrefix = (docId: string) => ['wiki', 'doc', docId] as const

/**
 * Search-result query key for the wikilink slash-menu picker + future
 * Cmd-K palette. Trimmed query string + kinds filter + limit are all
 * part of the key so distinct picker queries cache independently.
 *
 * The prefix `['wiki', 'search']` lets a future invalidation hook
 * (`invalidateQueries({ queryKey: WIKI_SEARCH_QUERY_KEY_PREFIX })`)
 * blow the whole search cache in one call if the tree mutates in a
 * way that could affect what would be in scope (e.g., a bulk delete).
 * Wave C3 doesn't need that invalidation today — `staleTime: 30s`
 * absorbs short-lived divergence — but the prefix is reserved.
 */
export const WIKI_SEARCH_QUERY_KEY_PREFIX = ['wiki', 'search'] as const

export const wikiSearchQueryKey = (
  query: string,
  kinds: readonly string[] | undefined,
  limit: number | undefined,
) => ['wiki', 'search', query, kinds ?? null, limit ?? null] as const

/**
 * Phase 3b — share-modal data fetch. Pulls the per-doc ACL list +
 * default-tenant-access + inherits-from-parent flag in one shot.
 *
 * Realtime invalidation (`wiki.doc.acl.changed` frame) blows this key
 * for the affected doc plus the prefix `wikiDocPrefix(docId)` to make
 * sure DocPage refetches meta in case the caller lost access.
 */
export const wikiDocAclQueryKey = (docId: string) => ['wiki', 'doc', docId, 'acl'] as const

export const wikiDocCommentsQueryKey = (docId: string) =>
  ['wiki', 'doc', docId, 'comments'] as const

/**
 * Confluence-style page comments — separate cache from the rail's
 * `'comments'` thread list so each surface invalidates independently.
 * The BE list route discriminates on `?kind=page` vs `?kind=thread`.
 */
export const wikiDocPageCommentsQueryKey = (docId: string) =>
  ['wiki', 'doc', docId, 'page-comments'] as const

/**
 * Doc-level reactions aggregate. WS frame `wiki.doc.reactions.updated`
 * invalidates this key so every open tab sees the live add/remove.
 */
export const wikiDocReactionsQueryKey = (docId: string) =>
  ['wiki', 'doc', docId, 'reactions'] as const

/**
 * Per-comment reactions aggregate (rail + page surfaces share this
 * cache shape; the discriminator is the `commentId`). WS frame
 * `wiki.comment.reactions.updated` invalidates this key.
 */
export const wikiCommentReactionsQueryKey = (commentId: string) =>
  ['wiki', 'comment', commentId, 'reactions'] as const

/**
 * Per-doc activity feed. Backs the rail's `<DocActivityPanel>`.
 * Invalidated by `wiki.doc.published`, `wiki.doc.acl.changed`, and
 * `wiki.tree.updated` frames whose `docId` / `changedNodeIds` match.
 */
export const wikiDocActivityQueryKey = (docId: string) =>
  ['wiki', 'doc', docId, 'activity'] as const
